import { Component, Input, OnInit, CUSTOM_ELEMENTS_SCHEMA, ViewChild, TemplateRef, OnChanges, SimpleChanges, ElementRef } from '@angular/core';
import { XSSheet, XSTable, APISheet } from 'src/app/layout/shell/shell.model';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TablesService } from 'src/app/core/service/table.service';
import { ToastService } from 'src/app/toast/toast.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ActiveSheetObservable } from 'src/app/core/service/observable.service';
import { ShellExt } from 'src/app/core/models/shell-ext';

@Component({
  selector: 'app-x-sheet',
  templateUrl: './x-sheet.component.html',
  styleUrls: ['./x-sheet.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class XSheetComponent implements OnInit, OnChanges {
  @Input() data: any;
  @ViewChild('downloadModal') downloadModal!: TemplateRef<any>;
  @ViewChild('deleteSheetModal') deleteSheetModal!: TemplateRef<any>;
  @ViewChild('spreadsheetContainer') spreadsheetContainer!: ElementRef;

  spreadsheet!: ShellExt;
  sheets: (XSSheet | APISheet)[] = [];
  activeSheet?: XSSheet | APISheet;
  activeTable: string = '';
  activeTableName: string = '';
  downloadLoading = {
    xlsx: false,
    csv: false
  };
  isXSEvtInitialized: boolean = false;
  locked: boolean = false;
  selectedSheetToDelete: string = '';
  newSheetLoading: boolean = false;

  constructor(
    private tableService: TablesService,
    private toastService: ToastService,
    public modalService: NgbModal,
    private activeSheetObservable: ActiveSheetObservable
  ) {}

  getSheetName(sheet: XSSheet | APISheet): string {
    return 'name' in sheet ? sheet.name : sheet.sheet_name;
  }

  ngOnInit() {
    this.initializeData();
  }

  ngAfterViewInit() {
    this.initSpreadsheet();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['data'] && !changes['data'].firstChange) {
      this.initializeData();
    }
  }

  private initializeData() {
    if (this.data) {
      this.activeTable = this.data.tableId;
      this.activeTableName = this.data.tableName;
      this.sheets = this.data.sheets as XSTable;
      if (this.data.sheet) {
        this.selectSheet(this.data.sheet);
      }
    }
  }

  private initSpreadsheet() {
    this.spreadsheet = new ShellExt("#x-spreadsheet-demo", {
      row: {
        len: 1000,
        height: 25,
      },
      col: {
        len: 100,
        width: 100,
        indexWidth: 60,
        minWidth: 60,
      },
      showToolbar: false,
      showBottomBar: false,
      style: {
        bgcolor: '#ffffff',
        align: 'left',
        valign: 'middle',
        textwrap: false,
        strike: false,
        underline: false,
        color: '#0a0a0a',
        font: {
          // @ts-ignore
          name: "proxima-nova",
          size: 10,
          bold: false,
          italic: false
        }
      }
    });
  }

  async switchSheetIfDifferent(sheet: XSSheet | APISheet) {
    if (!sheet) return;

    if (sheet.id !== this.activeSheet?.id) {
      const data = await this.tableService.getSheetData(sheet.id, true);
      if (data) {
        this.activeSheet = data.sheet;
        await this.selectSheet(this.activeSheet);
      }
    } else {
      sheet.showDropDown = !sheet.showDropDown;
      if (!sheet.loaded) {
        sheet.loaded = true;
      }
      await this.selectSheet(sheet);
    }
  }

  async selectSheet(sheet: XSSheet | APISheet) {
    if (!sheet) {
      return;
    }
    this.activeSheet = sheet;
    this.resetSheets();
    this.initXSSpreadsheetEvents();
    this.activeSheetObservable.setActiveSheet(sheet.id, this.activeTable);
    try {
      await this.spreadsheet.loadData(sheet);
    } catch (error) {
      console.error('Error loading sheet data:', error);
      this.toastService.showError('Error', 'Failed to load sheet data');
    }
  }

  resetSheets() {
    this.sheets?.forEach((sheet: XSSheet | APISheet) => {
      if (!this.activeSheet || this.activeSheet.id != sheet.id) {
        sheet.loaded = false;
        sheet.showDropDown = false;
      } else {
        sheet.loaded = true;
      }
    });
  }

  async initXSSpreadsheetEvents() {
    if (!this.isXSEvtInitialized) {
      //@ts-ignore
      this.spreadsheet.on('cell-edited', (value: string|number, rowIndex: number, columnIndex: number, oldValue: string|number, state: string) => {
        if (this.locked) return;
        if (this.activeTable && this.activeSheet && this.activeSheet.id) {
          const data = { value, oldValue };
          this.tableService.updateTableCell(this.activeTable, this.activeSheet.id, rowIndex, columnIndex, data).subscribe();
        }
      });

      await this.spreadsheet.change((data: any) => {
        if (this.locked) return;
        if (this.activeTable && this.activeSheet && this.activeSheet.id) {
          this.tableService.updateXSheetTable(this.activeTable, this.activeSheet.id, data).subscribe();
        }
      });
      this.isXSEvtInitialized = true;
    }
  }

  editSheetName(sheet: XSSheet | APISheet) {
    const sheetName = 'name' in sheet ? sheet.name : sheet.sheet_name;
    sheet.editedSheetName = sheetName;
    sheet.isSheetNameEdit = true;
  }

  stopEditingSheet(sheet: XSSheet | APISheet) {
    if (!sheet.editedSheetName) return;

    const updateData = 'name' in sheet 
      ? { name: sheet.editedSheetName }
      : { sheet_name: sheet.editedSheetName };

    const updateMethod = 'name' in sheet
      ? this.tableService.updateSheets(this.activeTable, sheet.id, updateData)
      : this.tableService.updateAPISheetTable(this.activeTable, sheet.id, updateData);

    updateMethod.subscribe((res: any) => {
      if ('name' in sheet) {
        sheet.name = sheet.editedSheetName!;
      } else {
        sheet.sheet_name = sheet.editedSheetName!;
      }
      sheet.isSheetNameEdit = false;
    });
  }

  uploadsheet(event: Event, sheet: XSSheet | APISheet) {
    const formData = new FormData();
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;

    formData.append('file', file);

    if (sheet && sheet.id) {
      this.tableService.uploadSheetFile(this.activeTable, sheet.id, formData)
        .subscribe((res: any) => {
          this.spreadsheet.loadData(res.data.XSheets);
          this.toastService.show('info', "File Uploaded Successfully", {
            classname: 'bg-white text-dark position-fixed bottom-0 end-0 mb-7 me-3'
          });
        });
    } else {
      this.tableService.uploadNewSheetFile(this.activeTable, formData)
        .subscribe((res: any) => {
          this.spreadsheet.loadData(res.data);
          this.toastService.show('info', "File Uploaded Successfully", {
            classname: 'bg-white text-dark position-fixed bottom-0 end-0 mb-7 me-3'
          });
        });
    }
  }

  duplicateSheet(sheet: XSSheet | APISheet) {
    this.tableService.duplicateSheet(this.activeTable, sheet)
      .subscribe((res: any) => {
        if (res.data?.sheets) {
          this.sheets = res.data.sheets;
          this.toastService.show('info', "Sheet is duplicated successfully.", {
            classname: 'bg-white text-dark position-fixed bottom-0 end-0 mb-7 me-3'
          });
        }
      });
  }

  deleteSheet(sheetId: string) {
    this.tableService.deleteSheet(this.activeTable, sheetId)
      .subscribe((res: any) => {
        const index = this.sheets.findIndex(s => s.id === sheetId);
        if (index > -1) {
          this.sheets.splice(index, 1);
        }
        this.toastService.show('info', "Sheet is deleted.", {
          classname: 'bg-white text-dark position-fixed bottom-0 end-0 mb-7 me-3'
        });
      });
  }

  createNewSheet() {
    if (!this.activeTable) {
      this.toastService.showError('Error', 'No active table selected');
      return;
    }

    this.newSheetLoading = true;
    this.tableService.createSheet(this.activeTable)
      .subscribe({
        next: async (res: any) => {
          if (res._createSheet?.sheetId) {
            const sheetData = await this.tableService.getSheetData(res._createSheet.sheetId, true);
            if (sheetData?.sheet) {
              this.sheets = [...this.sheets, sheetData.sheet];

              this.activeSheet = sheetData.sheet;
              await this.selectSheet(sheetData.sheet);

              this.toastService.show('info', 'New sheet created successfully', {
                classname: 'bg-white text-dark position-fixed bottom-0 end-0 mb-7 me-3'
              });
            }
          }
        },
        error: (error) => {
          console.error('Error creating new sheet:', error);
          this.toastService.showError('Error', 'Failed to create new sheet');
        },
        complete: () => {
          this.newSheetLoading = false;
        }
      });
  }

  cleanSpreadsheet() {
    this.spreadsheet.loadData({});
  }

  async downloadXlsx() {
    if (!this.activeTable) {
      this.toastService.showError('Error', 'No active table selected');
      return;
    }

    this.downloadLoading.xlsx = true;
    this.tableService.downloadXlsx(this.activeTable)
      .subscribe({
        next: (blob: any) => {
          this.tableService.triggerDownload(blob, `${this.activeTableName}.xlsx`);
          this.downloadLoading.xlsx = false;
          this.modalService.dismissAll();
        },
        error: (error) => {
          console.error('Error downloading XLSX:', error);
          this.downloadLoading.xlsx = false;
          this.toastService.showError('Error', 'Failed to download XLSX');
        }
      });
  }

  async downloadCsv() {
    if (!this.activeTable || !this.activeSheet?.id) {
      this.toastService.showError('Error', 'No active sheet selected');
      return;
    }

    this.downloadLoading.csv = true;
    this.tableService.downloadCsv(this.activeTable, this.activeSheet.id)
      .subscribe({
        next: (blob: any) => {
          this.tableService.triggerDownload(blob, `${this.activeTableName}.csv`);
          this.downloadLoading.csv = false;
          this.modalService.dismissAll();
        },
        error: (error) => {
          console.error('Error downloading CSV:', error);
          this.downloadLoading.csv = false;
          this.toastService.showError('Error', 'Failed to download CSV');
        }
      });
  }

  openDownloadModal() {
    if (!this.activeTable) {
      this.toastService.showError('Error', 'No active table selected');
      return;
    }
    this.modalService.open(this.downloadModal, { centered: true });
  }

  openDeleteModal(sheetId: string) {
    this.selectedSheetToDelete = sheetId;
    this.modalService.open(this.deleteSheetModal, { centered: true });
  }

  lockTable() {
    this.locked = true;
    //@ts-ignore
    this.spreadsheet.sheet.data.settings.mode = "read";
  }

  unlockTable() {
    this.locked = false;
    //@ts-ignore
    this.spreadsheet.sheet.data.settings.mode = "edit";
  }
}
